// Dòng tiền của từng thẻ trên trang Cơ chế lương — đối chiếu tay với
// `backend/pkg/engines/payroll/department_*.go`, mỗi thẻ một hàm `runXxx`.
//
// Quy tắc viết, đọc trước khi sửa:
//
//  1. **Nói phép tính, không nói giá trị.** "số khách × tiền một khách" được;
//     "25.000đ một giờ" thì không — số nằm trong ô trên màn, đổi mỗi lần lưu.
//     `redaction.test.ts` chặn chữ số đi kèm đơn vị tiền / phần trăm (cả lối
//     viết tắt "25k", "2tr"), kể cả ở trang đã nới, nên vi phạm là đỏ chứ không
//     im. Nhưng cổng không bắt hết — số viết bằng chữ, đơn vị đứng trước số vẫn
//     lọt — nên xanh KHÔNG có nghĩa là được viết số.
//  2. **`fields` phải là nhãn THẬT** trong `department-metadata.ts`. Bài kiểm
//     `salary-pay-flow.test.ts` đối chiếu từng chữ với nhãn của đúng thẻ đó:
//     đổi nhãn ô mà quên sửa hướng dẫn là đỏ, không phải là chữ lạc hậu nằm im.
//  3. **Thứ tự `steps` là thứ tự máy cộng**, khớp `PAY_SECTIONS`. Đây cũng là
//     thứ tự các khối trên màn, nên người đọc dò xuôi từ trên xuống được.

import type { PayFlow } from "../pay-flow-types";

// Định mức tháng (29/09/2026) đứng NGOÀI phép cộng tiền nên nằm ở ghi chú, không
// thành một bước: vẽ nó thành bước là bày ra một khoản không cộng vào đâu.
const QUOTA_NOTE =
  'Nhóm ô "Định mức tháng" không cộng trừ tiền của ai: máy chỉ đánh dấu người đạt hay dưới sàn trên bảng xếp hạng. Sàn tính bằng thạch lương thảo trên phần tiền trả theo việc, chia theo ngày công thật; người được xếp ít ca hơn tỷ lệ tối thiểu thì không bị xét. Công tắc tắt thì máy không xét ai.';

export const SALARY_PAY_FLOWS: PayFlow[] = [
  {
    dept: "photoAT",
    sum: "Lương cứng + tiền theo khách + phát sinh + tăng ca. Thưởng mốc khách thôi trả từ tháng lương 10/2026, nên thẻ không còn bảng mốc.",
    steps: [
      {
        section: "base",
        how: "Lấy thẳng từ ô lương cứng của đúng loại hợp đồng người đó đang ký. Lấy từ biểu giá này, không lấy từ hồ sơ nhân sự.",
        fields: ["Fulltime — Lương cứng", "Parttime — Lương cứng", "CTV — Lương cứng"],
      },
      {
        section: "perWork",
        how: "Đếm khách đã chụp trong tháng, tách làm ba loại đơn, mỗi loại nhân với ô tiền một khách của loại đó rồi cộng lại. Ba ô dưới đây là bộ của Fulltime; Parttime và CTV mỗi loại một bộ y hệt. Khách chụp từ 01/10/2026 mà bậc tay nghề đã bật cho người đó thì tính theo CÔNG: công của một khách là ô tiền một khách chia đơn giá một công, chia đều cho số thợ chụp của đơn, rồi nhân hệ số bậc và hệ số thâm niên của chính người đó. Người bậc G3 đủ thâm niên chụp một mình nhận đúng ô tiền một khách; chưa bật bậc thì mỗi người nhận đủ ô ấy, không chia.",
        fields: [
          "Fulltime — Mỗi khách",
          "Fulltime — Mỗi CV cơ bản",
          "Fulltime — Mỗi CV pro",
          "Phân loại đơn",
        ],
      },
      {
        section: "surcharge",
        how: "Mỗi mục bán thêm trên đơn dò xuống bảng, dừng ở dòng đầu khớp, rồi trả theo dòng ấy — thạch lương thảo quy ra đồng, hoặc số công nhân đơn giá dòng chính của đơn đó. Buổi chụp từ 01/10/2026, dòng tính bằng công và dòng có tỷ lệ công gốc là bội của công dòng chính: nhân hệ số bậc và thâm niên của chính người đó rồi chia đều cho số thợ chụp của đơn, trong thời gian rào không ai nhận thấp hơn phần cũ đã chia; dòng tính bằng tiền cũng vậy — số tiền ghi sẵn là mức của bậc G3, nhân hệ số bậc rồi chia đều số thợ chụp. Dòng lễ tân tích miễn phí không ra tiền cho thợ. Cơ chế bậc chưa chạm tới người đó thì dòng trả đủ như cũ, không chia.",
        fields: ["Phát sinh"],
      },
      {
        section: "overtime",
        how: "Giờ chấm công vượt chuẩn tháng thì phần vượt quy ra tiền theo lương cứng chia giờ chuẩn, rồi nhân hệ số tăng ca.",
        fields: ["Tăng ca — Hệ số giờ vượt chuẩn (toàn thời gian)"],
      },
    ],
    notes: [
      "Chỉ người toàn thời gian có tăng ca. Bán thời gian và cộng tác viên chưa có chuẩn giờ tháng nên máy bỏ qua, kể cả khi sổ chấm công có giờ.",
      "Đơn thuộc gói chưa nằm ở lớp nào trong Phân loại đơn thì khách của đơn ấy không rơi vào ô tiền nào — không phải lỗi ồn ào, mà là hụt im lặng.",
      "Người thuộc phòng khác sang chụp hộ ảnh thẻ chỉ nhận tiền theo khách, không kéo theo lương cứng của thẻ này. Từ tháng lương 10/2026, người làm hộ mà phòng gốc KHÔNG có lương cứng — thợ chụp profile, thợ chụp doanh nghiệp — ăn bộ ô của CTV, bất kể hồ sơ ghi loại hợp đồng nào; tháng lương trước đó giữ bộ ô theo loại hợp đồng. Phát sinh tính theo công của người ấy cũng lấy đúng bộ ô đó làm nền.",
      QUOTA_NOTE,
    ],
  },

  {
    dept: "makeupAT",
    sum: "Lương cứng + tiền theo khách ảnh thẻ + tiền theo buổi profile/doanh nghiệp + tiền theo gói + phát sinh + tăng ca. Thưởng mốc khách thôi trả từ tháng lương 10/2026, nên thẻ không còn bảng mốc.",
    steps: [
      {
        section: "base",
        how: "Lấy thẳng từ ô lương cứng của loại hợp đồng người đó đang ký.",
        fields: ["Fulltime — Lương cứng", "Parttime — Lương cứng", "CTV — Lương cứng"],
      },
      {
        section: "perWork",
        how: "Khách ảnh thẻ tính như thợ chụp: đếm theo ba loại đơn rồi nhân ô tiền một khách tương ứng. Ba ô dưới đây là bộ của Fulltime; Parttime và CTV mỗi loại một bộ y hệt. Khách chụp từ 01/10/2026 mà bậc tay nghề đã bật cho người đó cũng tính theo CÔNG như thợ chụp: ô tiền một khách chia đơn giá một công của trang điểm, chia đều cho số thợ trang điểm của đơn, rồi nhân hệ số bậc và hệ số thâm niên của chính người đó. Bậc dùng cho khách ảnh thẻ là bậc trang điểm Profile của chính người đó; người chưa có chứng chỉ ấy thì đứng ở bậc tạm như mọi thợ chưa có chứng chỉ.",
        fields: ["Fulltime — Mỗi khách", "Fulltime — Mỗi CV cơ bản", "Fulltime — Mỗi CV pro", "Phân loại đơn"],
      },
      {
        section: "perWork",
        how: "Mỗi đơn profile hoặc doanh nghiệp có tên người đó trả thêm một khoản theo hạng gói. Đơn profile chụp từ 01/10/2026 tính theo CÔNG: số công của hạng gói chia đều cho số thợ trang điểm của đơn, rồi nhân đơn giá một công và hệ số bậc của chính người đó. Đơn profile chụp trước ngày ấy và đơn doanh nghiệp trả mức theo hạng gói; đơn doanh nghiệp nhân thêm số buổi của đơn. Với đơn profile chụp từ ngày ấy, hạng đọc ô Hạng trang điểm được chọn trên đơn trước, đơn không có lựa chọn riêng mới theo gói. Hai ô số công, hai ô mức VÀ hai danh sách gói cơ bản · gói cao cấp quyết định hạng của gói đều nằm ở thẻ Makeup PF, không nằm ở thẻ này; đơn giá một công và hệ số bậc nằm ở thẻ Bậc tay nghề.",
      },
      {
        section: "perWork",
        how: "Đơn thuộc gói không nằm ở dòng ảnh thẻ, profile, doanh nghiệp hay buổi chụp Chụp Ảnh trả một mức theo gói, cộng phát sinh của đơn tính như một buổi chụp Chụp Ảnh; bảng mức và bảng phát sinh ấy đều nằm ở thẻ Makeup PF. Đơn có nhiều người cùng vai thì mức và phát sinh chia đều cho từng người.",
      },
      {
        section: "surcharge",
        how: "Mục bán thêm dò xuống bảng của thẻ, dừng ở dòng đầu khớp. Thẻ này có bảng riêng — khai ở thẻ thợ chụp không làm thợ trang điểm được tính. Buổi chụp từ 01/10/2026, dòng tính bằng công, dòng có tỷ lệ công gốc và dòng tính bằng tiền đều nhân hệ số bậc rồi chia đều cho số thợ trang điểm của đơn — số tiền ghi sẵn là mức của bậc G3; trong thời gian rào không ai nhận thấp hơn phần cũ đã chia. Dòng lễ tân tích miễn phí không ra tiền cho thợ. Cơ chế bậc chưa chạm tới người đó thì dòng trả đủ như cũ, không chia.",
        fields: ["Phát sinh"],
      },
      {
        section: "overtime",
        how: "Giống thợ chụp: phần giờ vượt chuẩn quy ra tiền theo lương cứng rồi nhân hệ số.",
        fields: ["Tăng ca — Hệ số giờ vượt chuẩn (toàn thời gian)"],
      },
    ],
    notes: [
      'Đơn thuộc gói trong danh sách "KHÔNG có trang điểm" thì thợ trang điểm không được tính khách cho đơn ấy, còn thợ chụp vẫn được tính bình thường.',
      "Buổi chụp của dòng Chụp Ảnh không trả theo đơn mà trả theo số mặt đã đánh, quy ra công — phần này đọc thẻ Bậc tay nghề.",
      QUOTA_NOTE,
    ],
  },

  {
    dept: "makeupPF",
    sum: "Tiền theo từng buổi profile/doanh nghiệp + tiền theo MẶT của job hợp đồng báo giá + tiền theo gói + phát sinh. Thẻ này không có ô lương cứng.",
    steps: [
      {
        section: "perWork",
        how: "Đơn profile chụp từ 01/10/2026 tính theo CÔNG. Hạng của đơn đọc ô Hạng trang điểm được chọn ngay trên đơn trước; đơn không có lựa chọn riêng thì theo gói: gói trong danh sách cao cấp lấy ô số công Gói cao cấp, gói trong danh sách cơ bản lấy ô Gói cơ bản. Gói chưa nằm ở danh sách nào là chưa xếp hạng: máy vẫn tạm tính theo ô Gói cơ bản, nhưng trang Bảng lương báo vàng và tháng chưa chốt được cho tới khi gói được xếp vào một trong hai danh sách, hoặc đơn đó được chọn ô Hạng trang điểm. Số công ấy chia đều cho số thợ trang điểm của đơn, rồi nhân đơn giá một công và hệ số bậc của chính người đó — hai thứ sau nằm ở thẻ Bậc tay nghề.",
        fields: ["Số công đơn Profile — Gói cơ bản", "Số công đơn Profile — Gói cao cấp", "Gói profile CƠ BẢN", "Gói profile CAO CẤP"],
      },
      {
        section: "perWork",
        how: "Hai ô mức theo đơn giữ hai việc. Một: đơn doanh nghiệp, đơn profile chụp trước ngày ấy và người chưa được bật cơ chế bậc vẫn trả mức theo hạng gói — gói trong danh sách cao cấp ăn ô Đơn cao cấp, gói ngoài ăn ô Đơn cơ bản; đơn doanh nghiệp nhân thêm số buổi của đơn. Hai: trong thời gian rào chuyển đổi, đơn tính theo công không thấp hơn mức này đã chia cho số thợ trang điểm của đơn.",
        fields: ["Đơn cơ bản", "Đơn cao cấp"],
      },
      {
        section: "perWork",
        how: "Buổi của hợp đồng báo giá KHÔNG trả theo buổi mà trả theo SỐ MẶT đã đánh, lấy từ sổ công của buổi: số mặt nhân ô dưới đây. Buổi chưa ai ghi sổ công thì máy tạm tính một mặt và gắn cờ nhắc — tháng còn cờ thì chưa chốt được.",
        fields: ["Doanh nghiệp — mỗi mặt"],
      },
      {
        section: "perWork",
        how: "Đơn thuộc gói không nằm ở dòng ảnh thẻ, profile, doanh nghiệp hay buổi chụp Chụp Ảnh trả theo số công của gói trong bảng: số công chia đều cho số thợ trang điểm của đơn, rồi nhân ô đơn giá một công của trang điểm buổi ở thẻ Bậc tay nghề; dòng sản phẩm đã gắn chuyên ngành thì nhân thêm hệ số bậc và thâm niên của chính người đó. Dòng còn khai bằng tiền theo cách cũ thì trả đúng mức tiền ấy, cũng chia đều. Phát sinh của đơn ấy tính như một buổi chụp Chụp Ảnh — xem khối phát sinh bên dưới.",
        fields: ["Mức trả theo gói"],
      },
      {
        section: "surcharge",
        how: "Mục bán thêm trên đơn profile và trên buổi chụp dò xuống bảng của thẻ, dừng ở dòng đầu khớp. Đơn theo gói dò đúng nhóm của buổi chụp, như một buổi chụp — chỉ phần miễn theo loại buổi là không áp, vì gói không mang loại buổi. Đơn profile và doanh nghiệp chụp từ 01/10/2026: dòng tính bằng công là bội của số công của đơn theo hạng trang điểm, nhân hệ số bậc, chia đều cho số thợ trang điểm; dòng tính bằng tiền lấy số tiền ghi sẵn làm mức bậc G3 rồi cũng nhân bậc và chia đều. Dòng lễ tân tích miễn phí không ra tiền cho thợ. Cơ chế bậc chưa chạm tới người đó thì dòng trả đủ như cũ, không chia.",
        fields: ["Phát sinh"],
      },
    ],
    notes: [
      "Hai ô số công, hai ô mức theo đơn và bảng mức theo gói của thẻ này nuôi tiền trên phiếu của thợ trang điểm ảnh thẻ — đó là chỗ khoản này thực sự ra tiền, nên sửa ở đây là đổi lương những người đó.",
      "Gói buổi 5 tiếng trả thợ trang điểm một số công cố định thay cho đếm mặt; giờ thêm tính theo luật mã gnca_them_gio.",
      "Ô Hạng trang điểm trên đơn chỉ thuộc về thợ trang điểm và chỉ áp cho đơn profile chụp từ 01/10/2026 — kể cả với người chưa được bật cơ chế bậc: người đó ăn ô Đơn cao cấp hay Đơn cơ bản theo đúng ô của đơn. Đơn doanh nghiệp và đơn chụp trước ngày ấy không đọc ô này; thợ chụp cũng không, phần của thợ chụp vẫn theo hai danh sách ở thẻ Photo PF.",
      "Gói nhóm của đơn profile đã gán loại buổi ở thẻ Bậc tay nghề chỉ đổi số công của thợ CHỤP. Với thợ trang điểm gói ấy vẫn phải nằm ở một trong hai danh sách của thẻ này; chưa xếp thì trang Bảng lương báo vàng như mọi gói chưa xếp hạng.",
      "Số công là số lẻ giữ ĐỦ chữ số: màn chỉ rút gọn khi hiện, máy vẫn tính bằng con số đầy đủ. Không gõ vào ô thì con số đầy đủ giữ nguyên qua mỗi lần lưu; gõ lại đúng con số rút gọn đang hiện là thay con số đầy đủ bằng con số rút gọn, và tiền của vài bậc có thể lệch một nấc làm tròn.",
      "Hai danh sách gói cơ bản và gói cao cấp ở thẻ này là bản RIÊNG, không dùng chung với thẻ Photo PF: xếp hạng gói ở thẻ kia không gỡ chữ vàng cho thợ trang điểm. Dòng đối chiếu cuối thẻ báo khi hai bản lệch nhau.",
      "Gói chưa có trong bảng mức theo gói thì dòng trên phiếu ghi \"Chưa có mức trả\" và tiền bằng không, phát sinh của đơn ấy cũng chờ — đó là chưa khai, không phải người đó không làm. Khai mức muộn vẫn kịp: tháng chưa chốt tự lấy bản khai gần tháng đó nhất cho những gói còn thiếu.",
      "Mức theo gói là mức của người bậc giữa đã đủ thâm niên: khi dòng đó được gắn chuyên ngành và bật bậc tay nghề, phần của từng người nhân hệ số bậc của chính họ, như buổi chụp Chụp Ảnh.",
    ],
  },

  {
    dept: "photoPF",
    sum: "Tiền theo từng buổi (profile · doanh nghiệp · buổi chụp) + tiền theo gói + khảo sát của đơn ngoại cảnh chụp trước 01/10/2026 + phần hưởng trên trang phục + phát sinh. Thẻ này không có lương cứng.",
    steps: [
      {
        section: "perWork",
        how: "Đơn profile chụp từ 01/10/2026 tính theo CÔNG: gói trong danh sách cao cấp lấy ô số công Gói cao cấp, gói trong danh sách cơ bản lấy ô Gói cơ bản. Gói chưa nằm ở danh sách nào là chưa xếp hạng: máy vẫn tạm tính theo ô Gói cơ bản, nhưng trang Bảng lương báo vàng và tháng chưa chốt được cho tới khi gói được xếp vào một trong hai danh sách. Số công ấy chia đều cho số thợ chụp của đơn, rồi nhân đơn giá một công và hệ số bậc của chính người đó — hai thứ sau nằm ở thẻ Bậc tay nghề. Người chưa có chứng chỉ bậc thì trong thời gian rào chuyển đổi đứng ở bậc ứng với mức cũ của mình: đang ở Bậc 1 thì tính như bậc G2, đang ở Bậc 2 thì tính như bậc G3.",
        fields: ["Số công đơn Profile — Gói cơ bản", "Số công đơn Profile — Gói cao cấp", "Gói profile CƠ BẢN", "Gói profile CAO CẤP"],
      },
      {
        section: "perWork",
        how: "Gói NHÓM của đơn profile (buổi tập thể) không hợp hạng nào trong hai hạng trên, vì cả hai đều là công của một người ngồi chụp. Gói như vậy gán loại buổi ở bảng bản đồ gói của thẻ Bậc tay nghề: đơn chụp từ 01/10/2026 của gói đã gán lấy số công của loại buổi ấy thay cho hai ô số công của thẻ này, rồi vẫn chia đều cho số thợ chụp, nhân đơn giá một công và hệ số bậc như trên. Số người của đơn nằm ngoài thang thì máy lấy nấc ít công nhất, không báo vàng. Gói đã gán coi như đã xếp hạng với thợ chụp; bốn ô mức theo đơn bên dưới vẫn là sàn của nó.",
        fields: ["Số công đơn Profile — Gói cơ bản", "Số công đơn Profile — Gói cao cấp"],
      },
      {
        section: "perWork",
        how: "Bốn ô mức theo đơn giữ hai việc. Một: đơn profile chụp trước ngày ấy và người chưa được bật cơ chế bậc vẫn trả theo hạng gói và bậc của người chụp — cao cấp hay cơ bản, mỗi hạng hai bậc. Hai: trong thời gian rào chuyển đổi, đơn tính theo công không thấp hơn mức này đã chia cho số thợ chụp của đơn.",
        fields: ["Cơ bản — Bậc 1", "Cơ bản — Bậc 2", "Cao cấp — Bậc 1", "Cao cấp — Bậc 2"],
      },
      {
        section: "perWork",
        how: "Đơn doanh nghiệp chụp TRƯỚC 01/10/2026, và người chưa được bật cơ chế bậc, trả đơn giá một buổi nhân số buổi của đơn; chụp ngoại cảnh ăn ô riêng. Đơn chụp từ 01/10/2026 tính theo CÔNG: đơn giá một buổi nhân số buổi rồi đổi ra công theo đơn giá một công của khối chụp — đó là công của MỘT thợ cho cả việc. Nhân số thợ chụp CẦN ghi trên báo giá của đơn, hoặc của hợp đồng gốc với buổi chụp của hợp đồng, ra công của cả việc; chia đều cho số thợ có tên trên đơn, rồi nhân hệ số bậc và thâm niên của chính người đó. Báo giá chưa khai số thợ cần thì máy coi số cần bằng số người đi. Điều chỉnh riêng theo người ở thẻ Theo nhân sự không nhân bậc, chia cùng tỉ lệ số cần trên số đi. Trong thời gian rào chuyển đổi, không ai nhận thấp hơn mức cũ đã nhân cùng tỉ lệ ấy. Đơn đã chốt giá riêng thì giá ấy thay giá chung, không nhân bậc và không đổi ra công; từ 01/10/2026 giá riêng cũng chia theo số cần trên số đi.",
        fields: ["Doanh nghiệp — nửa ngày tại studio", "Doanh nghiệp — nửa ngày ngoại cảnh"],
      },
      {
        section: "perWork",
        how: "Hợp đồng báo giá đi đường riêng: khảo sát KHÔNG tự cộng theo đơn mà đếm từ sổ công, mỗi lần đi ghi một dòng và trả theo ô dưới đây. Tiền rơi vào tháng của buổi gốc, không phải tháng đi khảo sát — khách chưa chốt hợp đồng thì lần đi đó không được trả.",
        fields: ["Khảo sát hợp đồng báo giá — mỗi lần đi"],
      },
      {
        section: "perWork",
        how: "Đơn thuộc gói không nằm ở dòng ảnh thẻ, profile, doanh nghiệp hay buổi chụp Chụp Ảnh trả theo số công của gói trong bảng: số công chia đều cho số thợ chụp của đơn, rồi nhân ô đơn giá một công của khối chụp ở thẻ Bậc tay nghề; dòng sản phẩm đã gắn chuyên ngành thì nhân thêm hệ số bậc và thâm niên của chính người đó. Dòng còn khai bằng tiền theo cách cũ thì trả đúng mức tiền ấy, cũng chia đều. Phát sinh của đơn ấy tính như một buổi chụp Chụp Ảnh — xem khối phát sinh bên dưới.",
        fields: ["Mức trả theo gói"],
      },
      {
        section: "surcharge",
        how: "Cộng tiền trang phục phát sinh của các đơn profile, buổi chụp và đơn theo gói trong tháng rồi nhân tỉ lệ hưởng.",
        fields: ["Phần hưởng trên tiền trang phục phát sinh"],
      },
      {
        section: "surcharge",
        how: "Mục bán thêm trên đơn profile và buổi chụp dò xuống bảng của thẻ, dừng ở dòng đầu khớp. Đơn theo gói dò đúng nhóm của buổi chụp, như một buổi chụp — chỉ phần miễn theo loại buổi là không áp, vì gói không mang loại buổi. Đơn profile và doanh nghiệp chụp từ 01/10/2026: dòng tính bằng công là bội của số công của đơn theo hạng gói, dòng có tỷ lệ công gốc lấy đúng tỷ lệ ấy nhân số công đó; cả hai nhân hệ số bậc và thâm niên rồi chia đều cho số thợ chụp của đơn, trong thời gian rào không ai nhận thấp hơn phần cũ đã chia. Dòng tính bằng tiền lấy số tiền ghi sẵn làm mức bậc G3 rồi cũng nhân bậc và chia đều. Dòng lễ tân tích miễn phí không ra tiền cho thợ. Cơ chế bậc chưa chạm tới người đó thì dòng trả đủ như cũ, không chia.",
        fields: ["Phát sinh"],
      },
    ],
    notes: [
      "Buổi chụp của dòng Chụp Ảnh không trả theo đơn mà quy ra CÔNG rồi nhân đơn giá công theo bậc — xem thẻ Bậc tay nghề.",
      "Hai danh sách gói cơ bản và gói cao cấp ở thẻ này chỉ xếp hạng cho thợ chụp; thẻ Makeup PF giữ bản riêng của thợ trang điểm. Dòng đối chiếu cuối thẻ báo khi hai bản lệch nhau.",
      "Số công là số lẻ giữ ĐỦ chữ số: màn chỉ rút gọn khi hiện, máy vẫn tính bằng con số đầy đủ. Không gõ vào ô thì con số đầy đủ giữ nguyên qua mỗi lần lưu; gõ lại đúng con số rút gọn đang hiện là thay con số đầy đủ bằng con số rút gọn, và tiền của vài bậc có thể lệch một nấc làm tròn.",
      "Tỷ lệ concept 2 của buổi chụp nhảy bậc theo TỔNG set phát sinh cả tháng của người đó, nên lương phần này chỉ chốt được cuối tháng, không tính ngay sau buổi chụp.",
      "Gói buổi 5 tiếng (loại buổi Buổi 5 tiếng ở thẻ Bậc tay nghề) trả mỗi thợ một số công cố định cho cả buổi, không tăng theo số concept và không đếm vào thang set tháng; giờ thêm tính theo luật mã gnca_them_gio.",
      "Set khách mua thêm bán theo gói mà chụp luôn trong buổi cũng tính là set phát sinh của thợ chụp, không cần khai mã ở mục Mã phát sinh; set đã bấm Tách lên lịch thì thợ của buổi tách nhận như đơn thường. Thợ trang điểm không có khoản theo set.",
      "Gói chưa có trong bảng mức theo gói thì dòng trên phiếu ghi \"Chưa có mức trả\" và tiền bằng không, phát sinh của đơn ấy cũng chờ — đó là chưa khai, không phải người đó không làm. Khai mức muộn vẫn kịp: tháng chưa chốt tự lấy bản khai gần tháng đó nhất cho những gói còn thiếu.",
      "Mức theo gói là mức của người bậc giữa đã đủ thâm niên: khi dòng đó được gắn chuyên ngành và bật bậc tay nghề, phần của từng người nhân hệ số bậc của chính họ, như buổi chụp Chụp Ảnh.",
      QUOTA_NOTE,
    ],
  },

  {
    dept: "sale",
    sum: "Lương cứng + hoa hồng. Mỗi loại hợp đồng một cách tính hoa hồng khác nhau.",
    steps: [
      {
        section: "base",
        how: "Lương cứng lấy từ hồ sơ nhân sự, không lấy từ thẻ này. Người đang thử việc bị nhân tỉ lệ thử việc; cộng tác viên không có lương cứng.",
        fields: ["Chung — Lương cứng khi thử việc (tỉ lệ)"],
      },
      {
        section: "commission",
        how: "Toàn thời gian: mọi doanh thu của mình (profile, doanh nghiệp, các dòng Chụp Ảnh, du lịch) nhân tỉ lệ của mình. Riêng doanh thu ảnh thẻ KHÔNG tính.",
        fields: ["Fulltime — Tỉ lệ hoa hồng"],
      },
      {
        section: "commission",
        how: "Bán thời gian: lấy toàn bộ doanh thu, nếu bật công tắc thì trừ phần doanh thu đầu được miễn, phần còn lại nhân tỉ lệ.",
        fields: [
          "Parttime — Tỉ lệ hoa hồng",
          "Parttime — Miễn hoa hồng phần doanh thu đầu",
          "Chung — Doanh thu đầu miễn hoa hồng (chỉ bán thời gian, khi bật công tắc)",
        ],
      },
      {
        section: "commission",
        how: "Cộng tác viên: chỉ doanh thu đơn ảnh thẻ nhân tỉ lệ của mình.",
        fields: ["CTV — Tỉ lệ hoa hồng"],
      },
      {
        section: "commission",
        how: "Trưởng nhóm đi bậc thang luỹ tiến: phần doanh thu trên mỗi mốc ăn tỉ lệ của mốc đó, không phải cả doanh thu ăn một tỉ lệ.",
        fields: ["Bậc thang hoa hồng Trưởng nhóm"],
      },
    ],
    notes: [
      "Từ tháng lương 11/2026, người có tên trong cơ cấu Phòng Kinh doanh (dù một ngày) lĩnh lương cứng theo vai trong cơ cấu và thu nhập bán hàng theo màn Phòng Kinh doanh (thẻ Bảng tổng), phiếu lương có khối riêng. Đơn nhận cọc lúc người đó có tên trong cơ cấu thì Phòng Kinh doanh trả, kể cả khi tiền về sau lúc người đó đã rời cơ cấu. Các ô của thẻ này chỉ còn tính: phần khách trả đủ trước 01/11/2026 mà hẹn vào tháng chụp từ 11/2026, và đơn lên trước ngày người nhận cọc vào cơ cấu. Phần Phòng Kinh doanh đã trả ở một tháng đã chốt mà sau đó đơn bị huỷ hoặc bỏ món (bỏ khoản phát sinh, bỏ nâng gói, huỷ đơn in) thì tháng kế tiếp trừ lại đúng phần người đó đã nhận; khách chỉ được hoàn một phần tiền mà món còn thì không trừ.",
      "Doanh thu của sale với đơn chụp từ 01/10/2026 là tiền khách ĐÃ TRẢ ĐỦ: giá gói sau giảm giá (chưa VAT) và phát sinh do sale ghi, mỗi phần vào tháng khách trả đủ tới nó, không sớm hơn tháng chụp. Đơn chụp trước đó vẫn tính giá gói niêm yết theo tháng chụp.",
      "Hồ sơ chưa ghi loại hợp đồng thì máy rơi về ô tỉ lệ dự phòng và tính trên toàn bộ doanh thu — vẫn ra tiền, nhưng không phải con số người ta trông đợi. Sửa hồ sơ chứ đừng sửa biểu giá.",
      "Điều chỉnh riêng theo người ở thẻ Theo nhân sự CỘNG vào tỉ lệ của ô, không thay thế nó.",
    ],
  },
  {
    dept: "sales_team",
    sum: "Lương cứng theo vai + hoa hồng phần mình giữ + thưởng vượt + chia túi tiểu đội + thưởng quản lý. Số lưu ở thẻ này áp từ lương tháng sau tháng lưu.",
    steps: [
      {
        section: "base",
        how: "Ai đã có hợp đồng ở HRM thì ăn đúng lương cứng ghi trong hợp đồng cả tháng, kể cả ngày giữ chức quản lý; tăng hay giảm lương riêng từng người làm bằng phụ lục hợp đồng ở HRM. Người chưa có hợp đồng thì mỗi ngày trong cơ cấu ăn mức của vai ngày đó: người giữ chức quản lý ăn mức của chức, người khác ăn mức Sales hoặc CSKH. Rồi chia theo ngày công, trừ nghỉ không lương như mọi người.",
        fields: ["Sales", "CSKH", "Tiểu đội trưởng", "Đội trưởng", "Leader miền"],
      },
      {
        section: "commission",
        how: "Mỗi phần tiền khách trả đủ nhân tỷ lệ của loại bán (đơn sales bán hay CSKH bán thêm), chia theo phần ghi công của từng người. Người bán giữ phần ở ô Phần người bán giữ; phần còn lại vào túi tiểu đội.",
        fields: ["Đơn sales bán", "CSKH bán thêm", "Phần người bán giữ"],
      },
      {
        section: "bonus",
        how: "Tiền xét mục tiêu của tiểu đội = tiền từng đơn nhân hệ số lần mua của khách (có món trọng điểm thì lấy hệ số cao hơn; đơn còn khiếu nại mở thì bỏ hệ số lần mua). Vượt mục tiêu tháng thì mỗi người được thưởng vượt = hoa hồng của mình × tỷ lệ vượt × ô Thưởng vượt × mức chất lượng của tiểu đội; người bán giữ phần như hoa hồng, phần còn lại vào túi.",
        fields: [
          "Thưởng vượt — Tỷ lệ trên hoa hồng",
          "Hệ số xét mục tiêu — Khách mua lần 2",
          "Hệ số xét mục tiêu — Khách mua lần 3",
          "Hệ số xét mục tiêu — Khách mua từ lần 4",
          "Hệ số xét mục tiêu — Món trọng điểm",
        ],
      },
      {
        section: "bonus",
        how: "Mức chất lượng của tiểu đội theo hai tiêu chí người ngoài tiểu đội nhập: chăm khách đúng hẹn, và khách hài lòng trên số khách đã hỏi (hỏi chưa đủ số tối thiểu mà quá hạn hỏi thì tiêu chí hài lòng tính đạt). Túi mở theo mức này, phần không mở công ty giữ. Túi chia theo ngày công cho thành viên tiểu đội; người có số lỗi xác nhận tới ngưỡng thì mất phần chia túi tháng đó.",
        fields: [
          "Mức chất lượng — Đạt cả hai tiêu chí",
          "Mức chất lượng — Đạt một tiêu chí",
          "Mức chất lượng — Không đạt tiêu chí nào",
          "Tiêu chí chất lượng — Chăm đúng hẹn tối thiểu",
          "Tiêu chí chất lượng — Khách hài lòng tối thiểu",
          "Tiêu chí chất lượng — Khách phải hỏi tối thiểu",
          "Tiêu chí chất lượng — Hạn hỏi khách (ngày của tháng sau)",
          "Chia túi — Số lỗi xác nhận thì mất phần chia túi",
        ],
      },
      {
        section: "bonus",
        how: "Tiểu đội trưởng: doanh thu thật của tiểu đội đi theo nấc — phần doanh thu nằm trong nấc nào ăn tỷ lệ của nấc đó, không nhân mức chất lượng. Đội trưởng và leader miền: tỷ lệ của mình nhân doanh thu thật các tiểu đội mình cầm, nhân mức chất lượng của từng tiểu đội. Chỉ tính tiền trả đủ trong những ngày giữ chức; chức trống thì công ty giữ.",
        fields: ["Nấc thưởng tiểu đội trưởng", "Thưởng quản lý — Đội trưởng", "Thưởng quản lý — Leader miền"],
      },
    ],
    notes: [
      "Số lưu ở thẻ này áp từ lương THÁNG SAU tháng lưu: lưu trong tháng nào thì lương tháng đó vẫn tính theo bản lưu trước. Các thẻ khác áp ngay trong tháng lưu.",
      "Chỉ người có tên trong cơ cấu Phòng Kinh doanh (từ tháng lương 11/2026) đi thẻ này; người ngoài cơ cấu vẫn đi thẻ Sale và CSKH.",
      "Lương tháng 1, 4, 7 và 10 xét lại thưởng vượt của quý trước: mục tiêu quý là cộng mục tiêu các tháng, tỷ lệ vượt tính trên cả quý, còn mức chất lượng và các ô của từng tháng giữ như lúc tính tháng đó. Ai có số tính lại cao hơn số đã nhận ở một tiểu đội thì được trả thêm phần chênh: người bán giữ phần theo ô Phần người bán giữ của tháng trả, phần còn lại vào túi quý của tiểu đội, chia theo ngày công cả quý — tháng có số lỗi xác nhận tới ngưỡng thì mất phần ngày công tháng đó. Thấp hơn thì không trừ lại. Quý 4/2026 chỉ gồm tháng 11 và 12; tháng nào của quý chưa chốt lương thì chưa xét được.",
      "Số tiền từng người, từng đơn xem ở màn Phòng Kinh doanh (thẻ Bảng tổng) và khối Phòng Kinh doanh trên phiếu lương.",
    ],
  },

  {
    dept: "reception",
    sum: "Lương cứng + lương theo giờ + hoa hồng bán thêm + tăng ca.",
    steps: [
      {
        section: "base",
        how: "Lương cứng lấy từ hồ sơ nhân sự, thẻ này không có ô đó.",
      },
      {
        section: "base",
        how: "Giờ làm lấy từ ca đã duyệt trong tháng, nhân ô lương theo giờ. Từ tháng lương 10/2026, tháng không có ca nào được duyệt thì lương giờ bằng không, trang Bảng lương báo đỏ và tháng chưa chốt được cho tới khi ca được duyệt — máy không đoán giờ. Người phụ trách quầy lễ tân và người kiểm soát chất lượng của lễ tân ăn lương cứng, ca họ đứng quầy không thành lương giờ, không có tăng ca.",
        fields: ["Lương theo giờ"],
      },
      {
        section: "commission",
        how: "Năm ô này chỉ còn trả cho phát sinh ghi TRƯỚC 01/09/2026 (tính lại tháng cũ): đếm lượt bán thêm theo năm nhóm, mỗi nhóm nhân ô tiền của nhóm đó rồi cộng lại. Từ 01/09/2026 lễ tân hưởng hoa hồng bán thêm chung ở thẻ Chung.",
        fields: [
          "Upsale — AT CV",
          "Upsale — Concept 2",
          "Upsale — Trang phục",
          "Upsale — DN nửa ngày",
          "Upsale — DN cả ngày",
        ],
      },
      {
        section: "overtime",
        how: "Giờ vượt chuẩn tháng nhân ô lương theo giờ rồi nhân phần cộng thêm. Lễ tân đã ăn lương theo giờ cho những giờ ấy nên đây chỉ là phần cộng THÊM, khác thợ chụp.",
        fields: ["Tăng ca — Phần cộng thêm cho giờ vượt chuẩn"],
      },
    ],
    notes: [
      "Ca có ghi giá riêng thì giờ của ca ấy tính theo giá riêng, nhưng tăng ca vẫn tính theo giá thường của người đó.",
      "Giờ chuẩn mỗi ngày và ngày công chuẩn mỗi tháng nằm ở thẻ Chung, dùng chung với thợ.",
    ],
  },

  {
    dept: "studioAssistant",
    sum: "Lương cứng + lương theo giờ + giờ job hợp đồng báo giá + tăng ca.",
    steps: [
      {
        section: "base",
        how: "Lương cứng lấy từ hồ sơ nhân sự, thẻ này không có ô đó. Người không có lương cứng thì khoản này bằng không, và đó là bình thường.",
      },
      {
        section: "base",
        how: "Giờ làm cộng từ các ca đã được ký duyệt trong tháng, nhân đơn giá một giờ. Đơn giá là ô dưới đây cộng phần điều chỉnh riêng của từng người ở màn Điều chỉnh riêng — điều chỉnh có thể là số trừ.",
        fields: ["Lương theo giờ"],
      },
      {
        section: "perWork",
        how: "Giờ đi buổi của hợp đồng báo giá KHÔNG lấy từ ca chấm công mà lấy từ sổ công của buổi: số phút đã ghi quy ra giờ rồi nhân ô dưới đây. Buổi chưa ai ghi thì máy tạm tính một giờ và gắn cờ nhắc.",
        fields: ["Giờ job doanh nghiệp"],
      },
      {
        section: "overtime",
        how: "Giờ vượt chuẩn tháng nhân đơn giá giờ rồi nhân phần cộng thêm. Giờ ấy đã được trả một lần ở khoản trên nên đây chỉ là phần cộng THÊM, giống lễ tân chứ không giống thợ chụp.",
      },
    ],
    notes: [
      "Ca chưa được ký duyệt thì không có giờ nào được tính, dù người đó đã đi làm — bảng lương hiện dải nhắc khi cả tháng không có ca nào duyệt.",
      "Đơn giá cộng xong mà ra số âm thì máy chặn lại ở không và gắn cờ nhắc, chứ không trả tiền âm.",
      "Phần cộng thêm cho giờ vượt chuẩn dùng chung ô của thẻ Lễ tân, thẻ này cố ý không có ô riêng.",
      "Giờ job hợp đồng báo giá KHÔNG vào phép tính tăng ca: nó là giờ của sổ công, không phải giờ chấm công, nên không đẩy ai qua chuẩn tháng.",
      "Ngoài buổi của hợp đồng báo giá, trợ lý không ăn tiền theo đơn hàng: đi cùng buổi chụp thường không sinh ra khoản nào trên phiếu của họ.",
    ],
  },

  {
    dept: "retouch",
    sum: "Lương cứng + tiền theo sản phẩm đã hoàn thành.",
    steps: [
      {
        section: "base",
        how: "Hai ô lương cứng cho hai nhóm: người phụ trách nhóm ăn ô trưởng nhóm, còn lại ăn ô cộng tác viên.",
        fields: ["Trưởng nhóm — Lương cứng", "Nhân viên (mọi hợp đồng, trừ người phụ trách nhóm) — Lương cứng"],
      },
      {
        section: "perWork",
        how: "Đếm số ảnh, thiết kế, album và ảnh ghép đã hoàn thành trong tháng, mỗi loại nhân ô của loại đó rồi cộng lại.",
        fields: ["Mỗi ảnh hoàn thành", "Mỗi thiết kế", "Mỗi album", "Mỗi ảnh ghép"],
      },
      {
        section: "perWork",
        how: "Ảnh sửa kèm theo gói trang phục tính thêm, trả theo đúng ô tiền mỗi ảnh.",
        fields: ["Mỗi ảnh hoàn thành"],
      },
      {
        section: "perWork",
        how: 'Ảnh của album ngoài đơn chuyển sang hậu kỳ nằm ở dòng riêng "Album ngoài đơn", trả theo đúng ô tiền mỗi ảnh. Chỉ tính khi album đã được hậu kiểm bấm "Đã kiểm", và vào lương của tháng bấm nút đó; bị "Bác công" thì không tính.',
        fields: ["Mỗi ảnh hoàn thành"],
      },
    ],
    notes: [
      "Số lượng không lấy từ đơn hàng mà lấy từ sổ việc hậu kỳ, nên đơn đã giao chưa chắc đã ghi công cho ai — kiểm sổ việc trước khi nghi biểu giá sai.",
    ],
  },

  {
    dept: "cskh",
    sum: "Lương cứng + hoa hồng trên doanh thu ảnh phát sinh và doanh thu in ảnh, cộng phần bù của tháng trước.",
    steps: [
      {
        section: "base",
        how: "Hai ô lương cứng: người phụ trách nhóm ăn ô trưởng nhóm, còn lại ăn ô nhân viên.",
        fields: ["Trưởng nhóm — Lương cứng", "Nhân viên — Lương cứng"],
      },
      {
        section: "commission",
        how: "Doanh thu ảnh phát sinh trong tháng nhân tỉ lệ của nó, doanh thu in ảnh nhân tỉ lệ của nó, cộng lại.",
        fields: ["Tỉ lệ hoa hồng ảnh phát sinh", "Tỉ lệ hoa hồng in ảnh"],
      },
    ],
    notes: [
      "Từ tháng lương 11/2026, người có tên trong cơ cấu Phòng Kinh doanh (dù một ngày) KHÔNG đi các ô của thẻ này: lương cứng theo vai trong cơ cấu và thu nhập bán hàng theo màn Phòng Kinh doanh (thẻ Bảng tổng); phần bù tháng trước vẫn cộng như cũ. Khoản đã trả ở một tháng đã chốt mà sau đó bị bỏ, đơn in bị huỷ hay đơn bị huỷ thì tháng kế tiếp trừ lại đúng phần đã nhận.",
      "Từ tháng lương 11/2026, doanh thu ảnh phát sinh và in ảnh do máy tự cộng từ những khoản khách đã trả đủ, ghi cho đúng người phụ trách; hai ô gõ tay của sổ tháng chỉ còn dùng cho các tháng trước đó.",
      "Phần bù của tháng trước cộng thẳng vào tổng, mang dấu âm hay dương theo sổ — không đi qua ô nào trên thẻ này.",
    ],
  },

  {
    dept: "accounting",
    sum: "Chỉ lương cứng, lấy đúng số trên hồ sơ nhân sự.",
    // Thẻ không còn ô nào (ô lương cứng đã rời màn): một khoản, không cộng dồn.
    additive: false,
    steps: [
      {
        section: "base",
        title: "Lương cứng",
        how: "Lương cứng là số ghi trên hồ sơ nhân sự, chỉ trả một suất ở phòng chính, chia theo số ngày còn làm khi vào hoặc nghỉ giữa tháng. Hồ sơ ghi 0 là người đó không có lương cứng — máy không tự điền số nào thay.",
      },
    ],
    notes: [
      "Thẻ này không có ô nào để sửa: muốn đổi lương cứng của người nào thì sửa hồ sơ nhân sự của người đó.",
      "Khoản cộng thêm hay trừ đi của từng người khai ở màn bảng lương, không khai ở đây.",
    ],
  },

  {
    dept: "marketing",
    sum: "Chỉ lương cứng, lấy đúng số trên hồ sơ nhân sự.",
    additive: false,
    steps: [
      {
        section: "base",
        title: "Lương cứng",
        how: "Giống thẻ Kế Toán: lương cứng là số trên hồ sơ nhân sự, chỉ một suất ở phòng chính, hồ sơ ghi 0 là không có lương cứng. Thẻ không có ô nào để sửa.",
      },
    ],
    notes: ["Thưởng theo chỉ tiêu khai ở màn bảng lương dưới dạng khoản cộng thêm, không khai ở đây."],
  },

  {
    dept: "_global",
    sum: "Thẻ này không ra lương cho riêng ai — nó nuôi những khoản chạy xuyên nhiều thẻ.",
    // Các mục dưới đây KHÔNG cộng lại thành lương một người: mỗi mục là một
    // khoản riêng, rơi vào phiếu của những người khác nhau.
    additive: false,
    steps: [
      {
        section: "commission",
        title: "Hoa hồng bán thêm",
        how: "Người của năm phòng — lễ tân, ảnh thẻ, Profile, doanh nghiệp, trang điểm — bán thêm được hưởng một tỉ lệ trên mục đã bán, cộng thưởng theo bậc thang tính trên tổng bán thêm cả tháng của người đó. Hậu kỳ, chăm sóc khách, trợ lý studio, kế toán, marketing không hưởng khoản này. Người phụ trách cơ sở hưởng theo vai từ tháng lương 10/2026, chỉ trên mục chính mình ghi; người phụ trách nhiều cơ sở không hưởng.",
        fields: ["Upsell — Tỉ lệ hoa hồng cơ bản", "Bậc thang hoa hồng Upsell"],
      },
      {
        section: "bonus",
        title: "Mốc động lực theo phòng",
        how: "Ba bảng mốc cho hậu kỳ, chăm sóc khách và lễ tân: dò mốc cao nhất đã đạt, trả một mốc. Từ tháng lương 11/2026, người có tên trong cơ cấu Phòng Kinh doanh ít nhất một ngày của tháng không nhận mốc doanh thu chăm sóc khách — doanh số đó đã vào hoa hồng đội.",
        fields: [
          "Động lực — Mốc số ảnh Retouch",
          "Động lực — Mốc doanh thu CSKH",
          "Động lực — Mốc lượt Upsale Lễ Tân",
        ],
      },
      {
        section: "bonus",
        title: "Thưởng dẫn đầu tháng",
        how: "Mỗi phòng trong danh sách có một người dẫn đầu tháng được thưởng, với điều kiện phòng đủ số người tối thiểu và người đó qua sàn sản lượng của phòng mình. Thưởng chỉ vào phiếu khi tháng đã khép — trong tháng, bảng xếp hạng chỉ cho biết ai đang dẫn. Một người chỉ nhận một danh hiệu dù dẫn đầu mấy phòng. Từ tháng lương 11/2026, người có tên trong cơ cấu Phòng Kinh doanh vẫn được xếp hạng nhưng không nhận thưởng dẫn đầu, và không ai được đôn lên thay.",
        fields: [
          "Quán quân phòng ban — Thưởng",
          "Quán quân phòng ban — Số nhân sự tối thiểu",
          "Quán quân phòng ban — Danh sách phòng tham gia",
          "Quán quân phòng ban — Sàn output theo phòng",
        ],
      },
      {
        section: "bonus",
        title: "Thưởng chuỗi ngày bán liên tiếp",
        how: "Chuỗi ngày bán hàng liên tiếp thưởng theo bậc thang và CỘNG DỒN các bậc đã qua, chặn ở trần tháng. Tính một lần cho một người, không tính lại theo từng phòng họ đứng. Từ tháng lương 11/2026, người có tên trong cơ cấu Phòng Kinh doanh không tính chuỗi.",
        fields: ["Chuỗi bán liên tiếp — Bậc thang", "Chuỗi bán liên tiếp — Trần thưởng/tháng", "Chuỗi bán liên tiếp — Phòng áp dụng"],
      },
      {
        section: "other",
        title: "Phụ cấp ca trưởng",
        how: "Mỗi ca của người đứng ca trưởng trả một khoản phụ cấp. Từ kỳ lương tháng 10/2026, một ca là một NGÀY người mang chức danh Trưởng ca vào ca tại cơ sở, hoặc một ca vận hành đã đóng mà họ làm ca trưởng — một ngày có cả hai vẫn chỉ là một ca. Người kiêm nhiều phòng vẫn chỉ nhận một lần cho một ca.",
        fields: ["Phụ cấp ca trưởng — Mỗi ca"],
      },
      {
        section: "other",
        title: "Phụ cấp ăn uống ê-kíp job",
        how: "Người đi buổi của hợp đồng báo giá có bật công tắc ăn uống được một khoản cho MỖI NGÀY, không phải mỗi buổi: một ngày đi hai buổi vẫn một suất. Đây là khoản bù chi phí nên không tính vào phần trăm nào của tháng.",
        fields: ["Phụ cấp ăn uống ê-kíp job — Mỗi người mỗi ngày"],
      },
      {
        section: "other",
        title: "Bù tiền đi lại của buổi job",
        how: "Tiền đi lại ghi tay từng người từng buổi trong sổ công của buổi, nên không có ô giá chung ở đây. Cũng là khoản bù chi phí, cộng thẳng vào phiếu và không tính vào phần trăm nào.",
      },
      {
        section: "other",
        title: "Chuẩn giờ công dùng chung",
        how: "Ba ô giờ công là chuẩn dùng chung: giờ chuẩn mỗi ngày nhân ngày công chuẩn ra chuẩn tháng, phần vượt chuẩn tháng mới là tăng ca. Trần giờ một lần vào ca chặn ca quên bấm ra thổi giờ lên.",
        fields: [
          "Giờ công — Giờ chuẩn mỗi ngày",
          "Giờ công — Ngày công chuẩn mỗi tháng",
          "Giờ công — Trần giờ một lần vào ca",
        ],
      },
    ],
    notes: [
      "Ba ô giờ công đổi là đổi mốc tăng ca của MỌI thẻ có tăng ca cùng lúc. Hệ số tăng ca thì nằm ở thẻ của từng vai, không nằm ở đây.",
      "Các khoản động lực cộng thêm lên trên, không thay khoản nào của thẻ vai. Thẻ Photo AT và Makeup AT cố ý không có mốc động lực: tới tháng lương 9/2026 bảng mốc khách của chúng đã trả phần đó, từ tháng lương 10/2026 thưởng mốc khách ảnh thẻ bỏ hẳn. Sàn xét dẫn đầu tháng của hai phòng này nằm ở ô Quán quân phòng ban — Sàn output theo phòng, tính bằng SỐ KHÁCH trong tháng; hậu kỳ tính bằng số ảnh, lễ tân bằng số lượt bán thêm, chăm sóc khách và kinh doanh bằng tiền.",
    ],
  },

  {
    dept: "craft",
    sum: "Thẻ này không ra lương trực tiếp — nó đổi CÁCH tính đơn giá của những thẻ làm nghề.",
    // Các mục dưới đây NHÂN vào cùng một đơn giá, không cộng với nhau — nối
    // chúng bằng dấu cộng là bày sai hẳn phép tính.
    additive: false,
    steps: [
      {
        section: "perWork",
        title: "Hệ số bậc — cái nhân vào đơn giá",
        how: "Mức đang trả hôm nay chính là mức của bậc G3 đủ thâm niên. Các bậc khác suy ra bằng phép nhân hệ số, nên cả phòng đang ở G3 thì bật lên bảng lương không đổi một đồng.",
        fields: [
          "Hệ số bậc — G1 Đạt",
          "Hệ số bậc — G2 Thạo",
          "Hệ số bậc — G3 Giỏi (gốc = mức hôm nay)",
          "Hệ số bậc — G4 Chuyên gia",
          "Hệ số bậc — G5 Bậc thầy",
        ],
      },
      {
        section: "perWork",
        title: "Hệ số thâm niên — nhân tiếp lên trên",
        how: "Người chưa đủ số tháng làm việc nhân thêm hệ số chưa đủ thâm niên của bậc mình. Hồ sơ chưa khai ngày vào làm thì coi như chưa đủ.",
        fields: [
          "Chưa đủ thâm niên — G1",
          "Chưa đủ thâm niên — G2",
          "Chưa đủ thâm niên — G3",
          "Chưa đủ thâm niên — G4",
          "Chưa đủ thâm niên — G5",
          "Số nền — Tháng làm việc tính là đủ thâm niên",
        ],
      },
      {
        section: "perWork",
        title: "Buổi chụp quy ra bao nhiêu công",
        how: "Buổi chụp quy ra CÔNG theo loại buổi, rồi công nhân đơn giá một công đã nhân hệ số bậc. Buổi nhiều người dò thang theo số người; buổi ngoại cảnh nhân thêm.",
        fields: [
          "Số nền — Đơn giá một công (khối chụp)",
          "Thang công — Buổi cá nhân",
          "Thang công — Buổi cặp đôi",
          "Thang công — Nhân khi chụp ngoại cảnh",
          "Thang công — Gia đình / bạn bè cao cấp theo số người",
          "Thang công — Gia đình cơ bản theo số người",
        ],
      },
      {
        section: "perWork",
        title: "Trang điểm buổi tính theo mặt",
        how: "Trang điểm buổi trả theo số mặt đã đánh, mỗi loại mặt một số công, nhân đơn giá một công. Buổi ngoại cảnh dùng đơn giá cố định riêng.",
        fields: [
          "Trang điểm buổi — Đơn giá một công",
          "Trang điểm buổi — Công một mặt nữ / người lớn",
          "Trang điểm buổi — Công một mặt nam",
          "Trang điểm buổi — Công một mặt trẻ dưới 12",
          "Trang điểm buổi — Ngoại cảnh: đơn giá cố định một công",
        ],
      },
      {
        section: "other",
        title: "Công tắc — ai bị chạm tới",
        how: "Cơ chế chỉ chạm người khi bật công tắc, cơ sở của người đó nằm trong danh sách cơ sở, VÀ người đó thuộc danh sách khối nghề HOẶC danh sách phòng ban. Danh sách để trống đọc là chưa bật chỗ nào, không phải bật tất cả.",
        fields: [
          "Công tắc — Bật cơ chế bậc",
          "Công tắc — Cơ sở áp dụng",
          "Công tắc — Khối nghề áp dụng",
          "Công tắc — Phòng ban áp dụng",
        ],
      },
      {
        section: "other",
        title: "Rào chuyển đổi — đỡ trong lúc giao thời",
        how: "Trong thời gian rào chuyển đổi, người chưa có chứng chỉ đứng ở bậc tạm và không ai bị trả thấp hơn mức hôm nay. Hết ngày rào thì phần hụt mới hiện ra thật.",
        fields: [
          "Chuyển đổi — Bậc tạm khi chưa có chứng chỉ",
          "Chuyển đổi — Bậc sau khi hết rào",
          "Chuyển đổi — Ngày hết rào",
          "Chuyển đổi — Trong thời gian rào, không ai thấp hơn hôm nay",
        ],
      },
    ],
    notes: [
      "Với khách ảnh thẻ và đơn Profile, thiếu dữ kiện thì máy trả Y NHƯ hôm nay, không đoán bậc thấp: chưa đọc được ngày chụp, dòng sản phẩm chưa gán chuyên ngành, mã bậc lạ — rơi về mức cũ. Buổi chụp Chụp Ảnh thì KHÁC: dòng này chưa từng có mức cũ, nên công tắc tắt hay không đọc được bậc là dòng ra không đồng, kèm lời nhắn trên bảng.",
      "Gói chưa có trong bản đồ loại buổi thì buổi của gói đó tạm tính như một buổi cá nhân trong tiệm và tháng chưa chốt được cho tới khi gói được gán. Hộp vàng trên bảng gọi tên từng gói đang thiếu.",
      "Buổi 5 tiếng: thợ chụp và thợ trang điểm mỗi người ăn số công cố định của ô buổi 5 tiếng, không đếm mặt, không cộng set concept phát sinh.",
      'Đơn profile chụp từ 01/10/2026 cũng tính theo công, nên hai ô "Số nền — Đơn giá một công (khối chụp)" và "Trang điểm buổi — Đơn giá một công" của thẻ này nuôi luôn tiền đơn profile. Số công của đơn profile thì nằm ở thẻ Photo PF và Makeup PF, không nằm ở đây.',
      "Khách ảnh thẻ chụp từ 01/10/2026 cũng đi qua hai ô đơn giá một công ấy, nhưng chỉ để quy ô tiền một khách ra số công in trên phiếu: đổi hai ô ấy thì số công đổi, tiền của khách ảnh thẻ KHÔNG đổi.",
      "Trang điểm ảnh thẻ không có thang bậc riêng: khách ảnh thẻ chụp từ 01/10/2026 chấm theo bậc trang điểm Profile của chính người đó.",
      "Bản đồ gói của thẻ này cũng nhận gói NHÓM của đơn profile (buổi tập thể): đơn profile chụp từ 01/10/2026 của gói đã gán thì thợ chụp lấy số công của loại buổi ấy, chia đều cho số thợ chụp của đơn. Số người của đơn nằm ngoài thang thì lấy nấc ít công nhất, không chặn — đơn profile không có ô khai số người. Thợ trang điểm của đơn ấy không đổi: vẫn theo hạng gói ở thẻ Makeup PF.",
      "Cơ sở lấy theo cơ sở của NGƯỜI, không theo nơi diễn ra buổi chụp — nếu không, cùng một thợ trong một tháng sẽ ăn hai cơ chế tuỳ hôm đó bị điều đi đâu.",
    ],
  },
];
